import { GeneralFilterData } from "@/pages/config/price-board/schema/price-board.schema";
import { AppColors } from "@/styles/colors";
import { Box, Button, Flex, Text } from "@chakra-ui/react";

import { Copy, Download } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Control, FieldValues, useWatch } from "react-hook-form";
import {
  drawCanvas,
  handleCopyHandler,
  handleDownload,
  loadImage,
} from "./price-board-canvas-handlers";

interface Props {
  control: Control<FieldValues>;
  dateData?: GeneralFilterData;
}

export const PriceBoardFlyerEditor = ({ control, dateData }: Props) => {
  const prices = useWatch({
    control,
    name: [
      "fechaPizarra",
      "sojaPizarra",
      "maizPizarra",
      "trigoPizarra",
      "fecha",
      "sojaMercado",
      "maizMercado",
      "trigoMercado",
      "sojaUnion",
      "maizUnion",
      "trigoUnion",
      "sojaInsumos",
      "maizInsumos",
      "trigoInsumos",
    ],
  });

  const canvasRefs = [
    useRef<HTMLCanvasElement | null>(null),
    useRef<HTMLCanvasElement | null>(null),
    useRef<HTMLCanvasElement | null>(null),
  ];

  const imagesRef = useRef<(HTMLImageElement | null)[]>([null, null, null]);

  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  useEffect(() => {
    for (const [index, ref] of canvasRefs.entries()) {
      const img = imagesRef.current[index];
      if (img && ref.current) drawCanvas(ref, img, index, prices, dateData);
    }
  }, []);

  useEffect(() => {
    const loadAllImages = async () => {
      // si ya están cargadas, no recargamos
      if (imagesRef.current.every(Boolean)) return;

      try {
        const loadedImages = await Promise.all([
          loadImage("/assets/PizarraDia.jpg"),
          loadImage("/assets/CierreMercado1.jpg"),
          loadImage("/assets/CierreMercado2.jpg"),
        ]);
        imagesRef.current = loadedImages; // guardamos en ref para hot-reload
      } catch (err) {
        console.error("Error cargando imágenes", err);
      }
    };
    loadAllImages();
  }, []);

  // Resize listener
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Redibujar en cambios
  useEffect(() => {
    if (imagesRef.current.every(Boolean)) {
      for (const [index, ref] of canvasRefs.entries()) {
        const img = imagesRef.current[index];
        if (img && ref.current) drawCanvas(ref, img, index, prices, dateData);
      }
    }
  }, [prices, windowWidth]);

  const canvasKeys = ["pizarra", "cierre1", "cierre2"];
  return (
    <Flex flexDirection={"column"}>
      <Text
        fontWeight={600}
        fontSize="24px"
        paddingTop="30px"
        paddingBottom="15px"
      >
        Previsualización Flyers
      </Text>
      <Flex gap="20px" flexWrap="wrap">
        {canvasRefs?.map((ref, index) => {
          return (
            <Box
              key={canvasKeys[index]}
              padding="16px"
              border={`1px solid ${AppColors.WHITE_BORDER_1}`}
              borderRadius="8px"
              position="relative"
            >
              <canvas
                id="flyer-editor-canvas"
                ref={ref}
                style={{
                  width: "100%",
                  height: "auto",
                  maxWidth: "300px",
                  borderRadius: "8px",
                }}
              />
              <Flex gap="12px">
                <Button
                  variant="outline"
                  onClick={() => handleCopyHandler(index, canvasRefs)}
                  marginTop="10px"
                  paddingX="8px"
                  border={`1px solid black`}
                >
                  <Copy color="black" />
                  <Text fontWeight={600}>Copiar</Text>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDownload(index, canvasRefs)}
                  marginTop="10px"
                  paddingX="8px"
                  border={`1px solid black`}
                  data-testid="price-board-flyer-download-button"
                >
                  <Download color="black" />
                  <Text fontWeight={600}>Descargar</Text>
                </Button>
              </Flex>
            </Box>
          );
        })}
      </Flex>
    </Flex>
  );
};
